import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import { AlertTriangle } from 'lucide-react';

const DietaryAlertsPanel = ({ alerts }) => {
  const [filter, setFilter] = useState('all');

  if (!alerts || alerts.length === 0) return null;

  const filtered = filter === 'all'
    ? alerts
    : alerts.filter(a => a.mealType === filter);

  const vegCount = alerts.filter(a => a.mealType === 'Veg').length;
  const nonVegCount = alerts.filter(a => a.mealType === 'Non-Veg').length;

  return (
    <Card className="border border-amber-200 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Dietary Alerts
          <span className="ml-auto text-sm font-normal text-gray-500">
            {alerts.length} customer(s) with dietary needs
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 mb-4">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-orange-500 hover:bg-orange-600' : ''}
          >
            All ({alerts.length})
          </Button>
          <Button
            variant={filter === 'Veg' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('Veg')}
            className={filter === 'Veg' ? 'bg-green-500 hover:bg-green-600' : ''}
          >
            Veg ({vegCount})
          </Button>
          <Button
            variant={filter === 'Non-Veg' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('Non-Veg')}
            className={filter === 'Non-Veg' ? 'bg-red-500 hover:bg-red-600' : ''}
          >
            Non-Veg ({nonVegCount})
          </Button>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Meal Type</TableHead>
                <TableHead className="w-[100px]">Meals</TableHead>
                <TableHead>Allergies</TableHead>
                <TableHead>Dietary Notes</TableHead>
                <TableHead>Food Preferences</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((alert, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Badge className={
                      alert.mealType === 'Non-Veg'
                        ? 'bg-red-100 text-red-700 border-red-200'
                        : 'bg-green-100 text-green-700 border-green-200'
                    }>
                      {alert.mealType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {['breakfast', 'lunch', 'dinner'].map(m => (
                        <Badge
                          key={m}
                          className={alert.activeMeals.includes(m)
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-200 text-gray-400'}
                        >
                          {m[0].toUpperCase()}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {alert.allergies.length > 0 ? (
                      <span className="text-sm font-medium text-red-700">
                        {alert.allergies.join(', ')}
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {alert.dietaryNotes ? (
                      <span className="text-sm text-gray-700">{alert.dietaryNotes}</span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {alert.foodPreferences.length > 0 ? (
                      <span className="text-sm text-gray-700">
                        {alert.foodPreferences.join(', ')}
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-400 py-6">
                    No dietary alerts for this filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default DietaryAlertsPanel;
